import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useLocation} from 'react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {Sparkles, CircleHelp, X, Send, Square, Plus, FileText} from 'lucide-react';
import {readSse} from '../ai-sse.mjs';
import {citationMarkdown} from '../ai-context.mjs';
import styles from './AiAssistant.module.css';
import AiAnswerActions, {type FeedbackBody} from './AiAnswerActions';
import {createFeedbackSender} from '../ai-feedback.mjs';

const AnswerMarkdown = memo(function AnswerMarkdown({text}: {text: string}) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={{a: ({children, href}) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>, img: () => null}}>{citationMarkdown(text)}</ReactMarkdown>;
});

type PageContext = {page_url: string; page_title: string};
const pageLabel = (context: PageContext, zh: boolean) => context.page_title.split(' | ')[0].trim() || (zh ? '当前页面' : 'Current page');
type Message = {role: 'user' | 'assistant'; text: string; context?: PageContext; messageId?: string; feedbackToken?: string};
export default function AiAssistant() {
  const location = useLocation();
  const zh = /(?:^|\/)zh-CN(?:\/|$)/.test(location.pathname);
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [pageContext, setPageContext] = useState<PageContext | null>(null);
  function currentPage(): PageContext {return {page_url: window.location.pathname + window.location.hash, page_title: document.title.slice(0, 200)};}
  function show(prefill = false) {
    launchFocus.current = document.activeElement as HTMLElement;
    setPageContext(currentPage()); setOpen(true);
    if (prefill) setQuestion(zh ? '告诉我关于本页的内容' : 'Tell me about this page');
  }
  const launchFocus = useRef<HTMLElement | null>(null);
  function close() {setOpen(false); launchFocus.current?.focus();}
  useEffect(() => {
    setMounted(true);
    const openPanel = () => show();
    window.addEventListener('weex:open-ai', openPanel);
    return () => window.removeEventListener('weex:open-ai', openPanel);
  }, [zh]);
  useEffect(() => {if (open) textarea.current?.focus({preventScroll: true});}, [open]);

  const [messages, setMessages] = useState<Message[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [threadId, setThreadId] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  const chat = useRef<HTMLElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const running = useRef(false);
  const feedbackSender = useRef<ReturnType<typeof createFeedbackSender> | null>(null);
  if (!feedbackSender.current) feedbackSender.current = createFeedbackSender();
  useEffect(() => () => {controller.current?.abort();}, []);
  useEffect(() => {if (chat.current) chat.current.scrollTop = chat.current.scrollHeight;}, [messages, status, error]);
  const suggestions = zh
    ? ['现货限价单 timeInForce 支持哪些值？', '如何生成 API 请求签名？', '如何订阅 WebSocket 深度行情？', '合约下单需要哪些参数？']
    : ['Which timeInForce values do spot limit orders support?', 'How do I sign an API request?', 'How do I subscribe to WebSocket depth?', 'Which parameters are required to place a futures order?'];
  const apiBase = () => import.meta.env.VITE_AI_API_BASE
    || ({'www.weex.com': 'https://api-spot.weex.com', 'stg-www.weex.tech': 'https://stg-spotpro-openapi.weex.tech'}[window.location.hostname] ?? '');
  const submitFeedback = useCallback((body: FeedbackBody) => {
    feedbackSender.current?.(`${apiBase().replace(/\/+$/, '')}/api/v3/ai/chat/feedback`, body);
  }, []);
  async function ask(value: string) {
    const message = value.trim();
    if (!message || running.current) return;
    if (message.length > 500) {setError(zh ? '问题最多 500 个字符，请精简后重试。' : 'Questions can contain up to 500 characters. Please shorten your question.'); return;}
    running.current = true;
    setBusy(true); setError(''); setStatus(zh ? '正在检索文档…' : 'Searching documentation…'); setQuestion('');
    const context = pageContext;
    setPageContext(null);
    setMessages(previous => [...previous, {role: 'user', text: message, context: context || undefined}, {role: 'assistant', text: ''}]);
    const abort = new AbortController();
    controller.current = abort;
    let completed = false;
    // Upstream may deliver many SSE deltas in a single batch. Reveal them at a
    // readable pace without blocking reads or inventing progress from the model.
    let pending = '';
    let finishReveal: (() => void) | undefined;
    const reveal = setInterval(() => {
      if (pending) {
        const characters = Array.from(pending);
        const count = Math.max(4, Math.min(24, Math.ceil(characters.length / 24)));
        const text = characters.slice(0, count).join('');
        pending = characters.slice(count).join('');
        setMessages(previous => previous.map((item, index) => index === previous.length - 1 ? {...item, text: item.text + text} : item));
      }
      if (!pending) finishReveal?.();
    }, 24);
    const cancelReveal = () => {clearInterval(reveal); pending = ''; finishReveal?.();};
    abort.signal.addEventListener('abort', cancelReveal, {once: true});
    try {
      const response = await fetch(`${apiBase().replace(/\/+$/, '')}/api/v3/ai/chat/stream`, {
        method: 'POST', credentials: 'include', signal: abort.signal,
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({message, threadId, language: zh ? 'cn' : 'en', context}),
      });
      if (!response.ok) {
        const reason = await response.json().catch(() => ({}));
        const dailyQuota = reason.error === 'Daily documentation quota reached';
        const detail = response.status === 503 ? (zh ? 'AI 服务尚未配置或暂时繁忙，请稍后重试。' : 'AI service is not configured or is busy. Please try later.')
          : response.status === 429 ? (dailyQuota
            ? (zh ? '当前 IP 在过去 24 小时内已提问 100 次，请稍后再试。' : 'This IP has reached 100 questions in the past 24 hours. Please try later.')
            : (zh ? '提问过于频繁，每 10 秒最多 3 次，请稍后重试。' : 'Up to 3 questions per 10 seconds. Please try again shortly.'))
          : [404, 409].includes(response.status) ? (zh ? '会话已失效或正在处理，请新建对话后重试。' : 'Conversation unavailable. Start a new chat and retry.')
          : (zh ? '暂时无法获取回答，请重试。' : 'Unable to get a response. Please retry.');
        throw new Error(detail);
      }
      if (!response.body || !response.headers.get('content-type')?.includes('text/event-stream')) throw new Error(zh ? '服务返回格式异常。' : 'Invalid response format.');
      for await (const raw of readSse(response.body)) {
        const event = JSON.parse(raw);
        if (event.type === 'error') throw new Error(zh ? '回答中断，请重试。' : 'Response interrupted. Please retry.');
        if (event.type === 'progress') setStatus(event.status === 'generating_answer' ? (zh ? '正在生成回答…' : 'Generating answer…') : (zh ? '正在检索文档…' : 'Searching documentation…'));
        if (typeof event.content === 'string') {
          setStatus('');
          pending += event.content;
        }
        if (event.done === true && typeof event.threadId === 'string') {
          setThreadId(event.threadId);
          if (typeof event.messageId === 'string' && typeof event.feedbackToken === 'string') setMessages(previous => previous.map((item, index) => index === previous.length - 1 ? {...item, messageId: event.messageId, feedbackToken: event.feedbackToken} : item));
          completed = true; break;
        }
      }
      if (pending) await new Promise<void>(resolve => {finishReveal = resolve;});
      if (!completed) throw new Error(zh ? '回答未完成，请重试。' : 'Response incomplete. Please retry.');
    } catch (cause) {
      if (abort.signal.aborted) setError(zh ? '已停止回答。' : 'Response stopped.');
      else setError(cause instanceof Error ? cause.message : (zh ? '请求失败，请重试。' : 'Request failed. Please retry.'));
      // A failed first response has no resumable conversation ID.
      setQuestion(message);
    } finally {
      cancelReveal();
      abort.signal.removeEventListener('abort', cancelReveal);
      controller.current = null; running.current = false; setBusy(false); setStatus('');
      textarea.current?.focus({preventScroll: true});
    }
  }
  if (!mounted) return null;
  return createPortal(<>
    {!open && <button type="button" className={styles.launcher} aria-label={zh ? '向 AI 询问本页内容' : 'Ask AI about this page'} title={zh ? 'AI 助手' : 'AI Assistant'} onClick={() => show(true)}><Sparkles size={18}/><span>{zh ? '向AI询问本页内容' : 'Ask AI about this page'}</span></button>}
    <aside hidden={!open} className={styles.panel} role="dialog" aria-label={zh ? 'AI 助手' : 'AI Assistant'} onKeyDown={event => {if (event.key === 'Escape') close();}}>
    <header className={styles.header}>
      <span><Sparkles size={19}/>{zh ? 'AI 助手' : 'AI Assistant'}</span>
      <div>{messages.length > 0 && <button type="button" disabled={busy} aria-label={zh ? '新建对话' : 'New chat'} title={zh ? '新建对话' : 'New chat'} onClick={() => {setMessages([]); setThreadId(null); setError(''); setQuestion(''); setPageContext(currentPage());}}><Plus size={19}/></button>}<button type="button" onClick={close} aria-label={zh ? '关闭' : 'Close'}><X size={20}/></button></div>
    </header>
    <section ref={chat} className={styles.chat} aria-label={zh ? '文档问答' : 'Documentation chat'} aria-live="polite" aria-busy={busy}>
      {!messages.length && <div className={styles.welcome}><Sparkles size={44}/><h2>{zh ? 'AI 助手' : 'AI Assistant'}</h2><p>{zh ? '关于 API 文档，你可以问我任何问题' : 'Ask me anything about the API documentation'}</p><div className={styles.suggestions}>{suggestions.map(item => <button type="button" key={item} onClick={() => void ask(item)}><CircleHelp size={14}/>{item}</button>)}</div></div>}
      {messages.map((item, index) => item.role === 'assistant' && !item.text && !busy ? null : <article key={index} className={`${styles.message} ${item.role === 'user' ? styles.user : styles.assistant}`} aria-label={item.role === 'user' ? (zh ? '问题' : 'Question') : (zh ? '回答' : 'Answer')}>
        {item.role === 'assistant' && <div className={styles.role}><span><Sparkles size={13}/>WEEX AI</span></div>}
        <div className={styles.markdown}>{item.role === 'user' ? <>{item.context && <><a className={styles.messageContext} href={item.context.page_url} title={`${pageLabel(item.context, zh)} · ${item.context.page_url}`}>Page:</a>{' '}</>}{item.text}</> : item.text ? <AnswerMarkdown text={item.text}/> : <p role="status" className={styles.status}>{status || (zh ? '正在检索文档…' : 'Searching documentation…')}</p>}</div>
        {item.role === 'assistant' && item.text && <AiAnswerActions text={item.text} messageId={item.messageId} feedbackToken={item.feedbackToken} zh={zh} open={open} submit={submitFeedback}/>}
      </article>)}
      {error && <p role="alert" className={styles.error}>{error}</p>}
    </section>
    <div className={styles.inputArea}>
    {pageContext && <div className={styles.context}><FileText size={14}/><a href={pageContext.page_url} title={pageContext.page_url}>{pageLabel(pageContext, zh)}</a><button type="button" aria-label={zh ? '移除页面上下文' : 'Remove page context'} onClick={() => setPageContext(null)}><X size={14}/></button></div>}
    <form className={styles.composer} onSubmit={event => {event.preventDefault(); void ask(question);}}>
      <label className={styles.srOnly} htmlFor="ai-question">{zh ? '你的文档问题' : 'Your documentation question'}</label>
      <textarea id="ai-question" ref={textarea} value={question} maxLength={500} rows={1} placeholder={zh ? '询问 WEEX API 文档…' : 'Ask about WEEX API documentation…'} onChange={event => setQuestion(event.target.value.slice(0, 500))} onKeyDown={event => {if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {event.preventDefault(); void ask(question);}}}/>
      {busy ? <button type="button" onClick={() => controller.current?.abort()} aria-label={zh ? '停止回答' : 'Stop response'}><Square size={18}/></button> : <button type="submit" disabled={!question.trim()} aria-label={zh ? '发送' : 'Send'}><Send size={19}/></button>}
    </form>
    <div className={styles.inputHint}><span>{zh ? '最多 500 个字符' : 'Up to 500 characters'}</span><span>{question.length}/500</span></div>
    </div>
    <p className={styles.note}>{zh ? 'AI 助手可能会出错，请核对引用文档。' : 'AI can make mistakes. Please verify against the cited docs.'}</p>
    </aside>
  </>, document.body);
}
